import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Divider, ListItemText, Menu, MenuItem, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { EditorCommand } from '../models/editor-command';

interface Props {
  /** Where the right-click happened, in client coordinates, or undefined when closed. */
  readonly position: { x: number; y: number } | undefined;
  readonly commands: readonly EditorCommand[];
  /** What was right-clicked, for the heading. Absent for a click on empty surface. */
  readonly subjectLabel?: string;
  readonly onClose: () => void;
}

/**
 * The actions for whatever was just right-clicked.
 *
 * A short list on purpose: the palette is where everything lives, and a context menu that
 * tries to be the palette is a menu nobody reads. What is here is what a right-click is
 * actually for — acting on the thing under the pointer without first finding the control
 * that does it.
 */
const CanvasContextMenu = ({ position, commands, subjectLabel, onClose }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <Menu
      open={position !== undefined}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position ? { top: position.y, left: position.x } : undefined}
      MenuListProps={{ dense: true, 'aria-label': String(t('floorplan-editor-context-menu')) }}
    >
      {subjectLabel && (
        <Typography variant="caption" color={StyleConstants.GREY_COLOR} sx={{ px: 2, py: 0.5, display: 'block' }}>
          {subjectLabel}
        </Typography>
      )}
      {subjectLabel && <Divider />}

      {commands.map((command) => (
        <MenuItem
          key={command.id}
          disabled={command.disabled}
          onClick={(): void => {
            onClose();
            command.run();
          }}
        >
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            {t(command.labelKey)}
          </ListItemText>
          {command.shortcut && (
            <Typography variant="caption" color={StyleConstants.GREY_COLOR} sx={{ pl: 3 }}>
              {command.shortcut}
            </Typography>
          )}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default CanvasContextMenu;
